/**
 * useOverlaySelection - Selection and editing state management
 * Elvis Aloha Collection - Arena Map Editor
 * 
 * Manages overlay selection, drag operations, resize handles, and vertex editing.
 */

import { useState, useCallback, useRef } from 'react';

// Resize handle positions
export const RESIZE_HANDLES = {
  NW: 'nw', NE: 'ne', SW: 'sw', SE: 'se',
  N: 'n', S: 's', E: 'e', W: 'w'
};

export const useOverlaySelection = () => {
  // Selection state
  const [selectedOverlayId, setSelectedOverlayId] = useState(null);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null); // 'move' | 'resize' | 'vertex'
  const [dragStart, setDragStart] = useState(null);
  
  // Resize state
  const [resizeHandle, setResizeHandle] = useState(null);
  
  // Vertex editing state
  const [editingVertexIndex, setEditingVertexIndex] = useState(null);
  
  // Locked overlays (cannot be edited)
  const lockedOverlaysRef = useRef(new Set());
  
  /**
   * Select an overlay
   */
  const selectOverlay = useCallback((overlay) => {
    if (!overlay) {
      setSelectedOverlayId(null);
      setShowPropertiesPanel(false);
      return;
    }
    
    setSelectedOverlayId(overlay.id);
    setShowPropertiesPanel(true);
  }, []);
  
  /**
   * Clear selection
   */
  const clearSelection = useCallback(() => {
    setSelectedOverlayId(null);
    setShowPropertiesPanel(false);
    setIsDragging(false);
    setDragStart(null);
    setResizeHandle(null);
    setEditingVertexIndex(null);
    setDragMode(null);
  }, []);
  
  /**
   * Start moving an overlay
   */
  const startMove = useCallback((overlay, imageCoords) => {
    if (!overlay) return false;
    if (lockedOverlaysRef.current.has(overlay.id)) return false;
    
    setIsDragging(true);
    setDragMode('move');
    setDragStart({
      x: imageCoords.x,
      y: imageCoords.y,
      overlay: JSON.parse(JSON.stringify(overlay)) // Deep clone
    });
    
    return true;
  }, []);
  
  /**
   * Start resizing an overlay
   */
  const startResize = useCallback((overlay, handle, imageCoords) => {
    if (!overlay || overlay.type !== 'rect') return false;
    if (lockedOverlaysRef.current.has(overlay.id)) return false;
    
    setIsDragging(true);
    setDragMode('resize');
    setResizeHandle(handle);
    setDragStart({
      x: imageCoords.x,
      y: imageCoords.y,
      overlay: JSON.parse(JSON.stringify(overlay))
    });
    
    return true;
  }, []);
  
  /**
   * Start editing a polygon vertex
   */
  const startVertexEdit = useCallback((overlay, vertexIndex, imageCoords) => {
    if (!overlay || overlay.type !== 'polygon') return false;
    if (lockedOverlaysRef.current.has(overlay.id)) return false;
    
    setIsDragging(true);
    setDragMode('vertex');
    setEditingVertexIndex(vertexIndex);
    setDragStart({
      x: imageCoords.x,
      y: imageCoords.y,
      overlay: JSON.parse(JSON.stringify(overlay))
    });
    
    return true;
  }, []);
  
  /**
   * Calculate new overlay position/dimensions during drag
   */
  const calculateDragUpdate = useCallback((currentCoords, overlay) => {
    if (!isDragging || !dragStart) return null;
    
    const dx = currentCoords.x - dragStart.x;
    const dy = currentCoords.y - dragStart.y;
    const original = dragStart.overlay;
    
    if (dragMode === 'move') {
      if (overlay.type === 'rect') {
        return {
          ...overlay,
          x: original.x + dx,
          y: original.y + dy
        };
      } else if (overlay.type === 'polygon') {
        const newPoints = original.points.map(p => ({
          x: p.x + dx,
          y: p.y + dy
        }));
        return { ...overlay, points: newPoints };
      } else if (overlay.type === 'marker') {
        return {
          ...overlay,
          x: original.x + dx,
          y: original.y + dy
        };
      }
    }
    
    if (dragMode === 'resize' && overlay.type === 'rect') {
      let { x, y, width, height } = original;
      
      // Handle corner resizing
      if (resizeHandle.includes('e')) {
        width = Math.max(10, original.width + dx);
      }
      if (resizeHandle.includes('w')) {
        const newWidth = Math.max(10, original.width - dx);
        x = original.x + (original.width - newWidth);
        width = newWidth;
      }
      if (resizeHandle.includes('s')) {
        height = Math.max(10, original.height + dy);
      }
      if (resizeHandle.includes('n')) {
        const newHeight = Math.max(10, original.height - dy);
        y = original.y + (original.height - newHeight);
        height = newHeight;
      }
      
      return { ...overlay, x, y, width, height };
    }
    
    if (dragMode === 'vertex' && overlay.type === 'polygon' && editingVertexIndex !== null) {
      const newPoints = [...overlay.points];
      newPoints[editingVertexIndex] = {
        x: currentCoords.x,
        y: currentCoords.y
      };
      return { ...overlay, points: newPoints };
    }
    
    return overlay;
  }, [isDragging, dragStart, dragMode, resizeHandle, editingVertexIndex]);
  
  /**
   * End drag operation and return change metadata
   */
  const endDrag = useCallback(() => {
    const result = {
      wasEditing: isDragging,
      dragMode,
      startState: dragStart?.overlay,
      resizeHandle,
      vertexIndex: editingVertexIndex
    };
    
    setIsDragging(false);
    setDragStart(null);
    setResizeHandle(null);
    setEditingVertexIndex(null);
    setDragMode(null);
    
    return result;
  }, [isDragging, dragMode, dragStart, resizeHandle, editingVertexIndex]);
  
  /**
   * Lock an overlay (prevent editing)
   */
  const lockOverlay = useCallback((overlayId) => {
    lockedOverlaysRef.current.add(overlayId);
  }, []);
  
  /**
   * Unlock an overlay
   */
  const unlockOverlay = useCallback((overlayId) => {
    lockedOverlaysRef.current.delete(overlayId);
  }, []);
  
  /**
   * Check if an overlay is locked
   */
  const isLocked = useCallback((overlayId) => {
    return lockedOverlaysRef.current.has(overlayId);
  }, []);
  
  /**
   * Insert a new vertex into a polygon
   */
  const insertPolygonVertex = useCallback((overlay, afterIndex, point) => {
    if (!overlay || overlay.type !== 'polygon') return null;
    
    const newPoints = [...overlay.points];
    newPoints.splice(afterIndex + 1, 0, point);
    
    return { ...overlay, points: newPoints };
  }, []);
  
  /**
   * Remove a vertex from a polygon (minimum 3 vertices required)
   */
  const removePolygonVertex = useCallback((overlay, vertexIndex) => {
    if (!overlay || overlay.type !== 'polygon') return null;
    if (overlay.points.length <= 3) return null; // Can't remove below 3
    
    const newPoints = overlay.points.filter((_, i) => i !== vertexIndex);
    return { ...overlay, points: newPoints };
  }, []);
  
  return {
    // Selection
    selectedOverlayId,
    setSelectedOverlayId,
    showPropertiesPanel,
    setShowPropertiesPanel,
    selectOverlay,
    clearSelection,
    
    // Dragging
    isDragging,
    dragMode,
    dragStart,
    startMove,
    startResize,
    startVertexEdit,
    calculateDragUpdate,
    endDrag,
    
    // Resize
    resizeHandle,
    
    // Vertex editing
    editingVertexIndex,
    insertPolygonVertex,
    removePolygonVertex,
    
    // Locking
    lockOverlay,
    unlockOverlay,
    isLocked
  };
};

export default useOverlaySelection;
